import { Client } from 'urql';
import { filter, map, pipe, subscribe } from 'wonka';

import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import {
  GenerativeTaskQuery,
  GenerativeTaskQueryDocument,
  GenerativeTaskQueryVariables,
} from '@/components/LyearnAI/graphql/queries/generated/generativeTaskQuery';
import { GenerativeTaskSubscriptionDocument } from '@/components/LyearnAI/graphql/subscriptions/generated/generativeTaskSubscription';
import { GenerativeTaskStatusEnum } from '@/types/schema';

export type TaskStreamStatus =
  | 'subscribing'
  | 'subscription_retrying'
  | 'polling'
  | 'completed'
  | 'cancelled'
  | 'failed'
  | 'timed_out'
  | 'error';

export type TaskStreamEvent = {
  generativeTask: GenerativeTaskFragment;
  status: TaskStreamStatus;
  isComplete: boolean;
};

export type TaskStreamOptions = {
  // Callbacks
  onData?: (event: TaskStreamEvent) => void;
  onComplete?: (generativeTask: GenerativeTaskFragment) => void;
  onError?: (error: Error, status: TaskStreamStatus) => void;
  /** Maximum time to wait for subscription events before timeout (default: 45000ms) */
  subscriptionTimeoutMs?: number;
  /** Maximum number of times to retry failed subscription attempts (default: 2) */
  subscriptionMaxRetries?: number;
  /** Interval between polling attempts in milliseconds (default: 15000ms) */
  pollingIntervalMs?: number;
  /** Maximum number of polling attempts before giving up (default: 6) */
  pollingMaxAttempts?: number;
  /** Initial delay before starting polling in milliseconds (default: 30000ms) */
  pollingStartDelayMs?: number;
  /** Global timeout for the entire streaming operation in milliseconds (default: 120000ms / 2 minutes) */
  globalTimeoutMs?: number;
};

const DEFAULT_SUBSCRIPTION_TIMEOUT_MS = 45000;
const DEFAULT_MAX_SUBSCRIPTION_RETRIES = 2;
const DEFAULT_POLLING_INTERVAL_MS = 15000;
const DEFAULT_MAX_POLLING_ATTEMPTS = 6;
const DEFAULT_POLLING_START_DELAY_MS = 30000;
const DEFAULT_GLOBAL_TIMEOUT_MS = 120000;

/**
 * Creates a resilient stream for monitoring generative task status
 * with automatic fallback from subscription to query.
 *
 * @param client - urql Client instance
 * @param generativeTaskId - ID of the task to monitor
 * @param options - Configuration including callbacks, timeout, and max retries
 * @returns Object with subscribe function and unsubscribe handle
 */
/**
 * Check if generative task has reached a terminal state
 */
function isGenerativeTaskComplete(status: GenerativeTaskStatusEnum): boolean {
  return (
    status === GenerativeTaskStatusEnum.Completed ||
    status === GenerativeTaskStatusEnum.Cancelled ||
    status === GenerativeTaskStatusEnum.Failed
  );
}

/**
 * Map generative task status to stream status
 */
function getStreamStatus(
  generativeTaskStatus: GenerativeTaskStatusEnum,
  source: 'subscription' | 'polling',
): TaskStreamStatus {
  switch (generativeTaskStatus) {
    case GenerativeTaskStatusEnum.Completed:
      return 'completed';
    case GenerativeTaskStatusEnum.Cancelled:
      return 'cancelled';
    case GenerativeTaskStatusEnum.Failed:
      return 'failed';
    default:
      return source === 'polling' ? 'polling' : 'subscribing';
  }
}

export function createResilientGenerativeTaskStream(
  client: Client,
  generativeTaskId: string,
  options: TaskStreamOptions = {},
) {
  // Configuration with defaults
  const subscriptionTimeoutMs = options.subscriptionTimeoutMs ?? DEFAULT_SUBSCRIPTION_TIMEOUT_MS;
  const subscriptionMaxRetries = options.subscriptionMaxRetries ?? DEFAULT_MAX_SUBSCRIPTION_RETRIES;
  const pollingIntervalMs = options.pollingIntervalMs ?? DEFAULT_POLLING_INTERVAL_MS;
  const pollingMaxAttempts = options.pollingMaxAttempts ?? DEFAULT_MAX_POLLING_ATTEMPTS;
  const pollingStartDelayMs = options.pollingStartDelayMs ?? DEFAULT_POLLING_START_DELAY_MS;
  const globalTimeoutMs = options.globalTimeoutMs ?? DEFAULT_GLOBAL_TIMEOUT_MS;

  // Shared state between streams
  let isCompleted = false;
  let lastKnownGenerativeTask: GenerativeTaskFragment | null = null;
  let subscriptionRetryCount = 0;
  let subscriptionActive = false;
  let pollingAttemptCount = 0;
  let pollingActive = false;
  let heartbeatTimeout: NodeJS.Timeout | null = null;
  let pollingIntervalId: NodeJS.Timeout | null = null;
  let pollingStartTimeoutId: NodeJS.Timeout | null = null;
  let globalTimeoutId: NodeJS.Timeout | null = null;
  let subscriptionUnsubscribe: (() => void) | null = null;

  /**
   * Cleanup all streams and timers
   */
  const cleanupAllStreams = () => {
    // Subscription cleanup
    if (subscriptionUnsubscribe) {
      subscriptionUnsubscribe();
      subscriptionUnsubscribe = null;
    }
    if (heartbeatTimeout) {
      clearTimeout(heartbeatTimeout);
      heartbeatTimeout = null;
    }

    // Polling cleanup
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }
    if (pollingStartTimeoutId) {
      clearTimeout(pollingStartTimeoutId);
      pollingStartTimeoutId = null;
    }

    // Timeout cleanup
    if (globalTimeoutId) {
      clearTimeout(globalTimeoutId);
      globalTimeoutId = null;
    }

    // Reset state
    subscriptionActive = false;
    pollingActive = false;
  };

  /**
   * Emit error and cleanup
   */
  const emitError = (error: Error, status: TaskStreamStatus) => {
    cleanupAllStreams();
    options.onError?.(error, status);
  };

  /**
   * Emit data to consumer
   */
  const emitData = (generativeTask: GenerativeTaskFragment, source: 'subscription' | 'polling') => {
    if (isCompleted) return true; // Already completed, ignore

    lastKnownGenerativeTask = generativeTask;
    const generativeTaskComplete = isGenerativeTaskComplete(generativeTask.status);
    const streamStatus = getStreamStatus(generativeTask.status, source);

    options.onData?.({ generativeTask, status: streamStatus, isComplete: generativeTaskComplete });

    if (generativeTaskComplete) {
      isCompleted = true;
      cleanupAllStreams();

      if (generativeTask.status === GenerativeTaskStatusEnum.Completed) {
        options.onComplete?.(generativeTask);
      } else if (generativeTask.status === GenerativeTaskStatusEnum.Failed) {
        emitError(new Error('Generative task failed'), 'failed');
      } else if (generativeTask.status === GenerativeTaskStatusEnum.Cancelled) {
        emitError(new Error('Generative task cancelled'), 'cancelled');
      }
    }

    return generativeTaskComplete;
  };

  /**
   * Execute a query to fetch generative task status
   */
  const executeQuery = () => {
    if (isCompleted) return Promise.resolve();

    pollingAttemptCount++;

    return client
      .query<GenerativeTaskQuery, GenerativeTaskQueryVariables>(
        GenerativeTaskQueryDocument,
        { filters: { generativeTaskId } },
        { requestPolicy: 'network-only' },
      )
      .toPromise()
      .then(({ data, error }) => {
        if (isCompleted) return; // Completed by subscription, ignore query result

        if (error) {
          // Don't fail completely on query error - log warning and continue
          console.warn('[TaskStream] Query failed:', error.message);
          return;
        }

        const generativeTask = data?.generativeTask?.generativeTask;
        if (!generativeTask) {
          console.warn('[TaskStream] Query returned no generative task data');
          return;
        }

        emitData(generativeTask, 'polling');
      })
      .catch((err) => {
        if (!isCompleted) {
          console.warn('[TaskStream] Query error:', err);
        }
      });
  };

  /**
   * Stream 2: Polling Stream
   * Periodically queries generative task status as safety net
   */
  const startPollingStream = () => {
    if (isCompleted || pollingActive) return;

    pollingActive = true;
    pollingAttemptCount = 0;

    const poll = async () => {
      if (isCompleted || pollingAttemptCount >= pollingMaxAttempts) {
        if (pollingIntervalId) {
          clearInterval(pollingIntervalId);
          pollingIntervalId = null;
        }
        pollingActive = false;
        return;
      }

      await executeQuery();
    };

    // Start polling immediately, then repeat at interval
    poll();
    pollingIntervalId = setInterval(poll, pollingIntervalMs);
  };

  /**
   * Stream 1: Subscription Stream
   * Real-time updates with automatic retry on stall
   */
  const startSubscriptionStream = () => {
    if (isCompleted) return;

    subscriptionActive = true;

    const resetHeartbeat = () => {
      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      heartbeatTimeout = setTimeout(() => {
        if (isCompleted) return;

        // Subscription stalled - restart if retries remaining
        if (subscriptionRetryCount < subscriptionMaxRetries) {
          subscriptionRetryCount++;
          console.log(
            `[TaskStream] Subscription stalled, retrying (${subscriptionRetryCount}/${subscriptionMaxRetries})`,
          );

          // Unsubscribe current subscription
          if (subscriptionUnsubscribe) {
            subscriptionUnsubscribe();
            subscriptionUnsubscribe = null;
          }

          // Emit retry status
          if (lastKnownGenerativeTask) {
            options.onData?.({
              generativeTask: lastKnownGenerativeTask,
              status: 'subscription_retrying',
              isComplete: false,
            });
          }

          // Restart subscription
          startSubscriptionStream();
        } else {
          console.log('[TaskStream] Subscription max retries reached');
          subscriptionActive = false;
          if (subscriptionUnsubscribe) {
            subscriptionUnsubscribe();
            subscriptionUnsubscribe = null;
          }
        }
      }, subscriptionTimeoutMs);
    };

    const subscriptionPipe = pipe(
      client.subscription(
        GenerativeTaskSubscriptionDocument,
        { data: { generativeTaskId } },
        { requestPolicy: 'network-only' },
      ),
      map(({ data, error }) => ({ generativeTask: data?.generativeTask?.generativeTask, error })),
      filter(({ generativeTask, error }) => generativeTask !== undefined || error !== undefined),
    );

    const subscription = subscribe<{ generativeTask: any; error: any }>((result) => {
      if (isCompleted) return;

      resetHeartbeat();

      if (result.error) {
        // Subscription error - count as retry and restart if possible
        console.warn('[TaskStream] Subscription error:', result.error.message);

        if (subscriptionRetryCount < subscriptionMaxRetries) {
          subscriptionRetryCount++;
          console.log(
            `[TaskStream] Restarting after error (${subscriptionRetryCount}/${subscriptionMaxRetries})`,
          );

          if (subscriptionUnsubscribe) {
            subscriptionUnsubscribe();
            subscriptionUnsubscribe = null;
          }

          setTimeout(() => startSubscriptionStream(), 1000); // Brief delay before retry
        } else {
          subscriptionActive = false;
          if (subscriptionUnsubscribe) {
            subscriptionUnsubscribe();
            subscriptionUnsubscribe = null;
          }
        }
        return;
      }

      if (result.generativeTask) {
        emitData(result.generativeTask, 'subscription');
      }
    })(subscriptionPipe);

    subscriptionUnsubscribe = subscription.unsubscribe;
    resetHeartbeat();
  };

  /**
   * Stream 3: Global Timeout Stream
   * Absolute upper bound on total operation time
   */
  const startTimeoutStream = () => {
    globalTimeoutId = setTimeout(() => {
      if (isCompleted) return;

      const diagnosticInfo = {
        lastStatus: lastKnownGenerativeTask?.status,
        subscriptionRetries: subscriptionRetryCount,
        pollingAttempts: pollingAttemptCount,
        subscriptionActive,
        pollingActive,
      };

      console.error('[TaskStream] Global timeout reached', diagnosticInfo);
      emitError(
        new Error(
          `Global timeout reached after ${globalTimeoutMs}ms. Generative task still in progress.`,
        ),
        'timed_out',
      );
    }, globalTimeoutMs);
  };

  /**
   * Start all streams
   */
  const execute = () => {
    // Stream 1: Start subscription immediately
    startSubscriptionStream();

    // Stream 3: Start global timeout immediately
    startTimeoutStream();

    // Stream 2: Start polling after delay
    pollingStartTimeoutId = setTimeout(() => {
      startPollingStream();
    }, pollingStartDelayMs);

    // Return cleanup function
    return () => {
      cleanupAllStreams();
    };
  };

  return {
    subscribe: execute,
  };
}

/**
 * Three Parallel Streams Implementation - Pure Stream Factory (No React)
 *
 * Creates a resilient stream for monitoring generative AI tasks using three parallel streams architecture.
 *
 * Architecture:
 * - Stream 1 (Subscription): Primary real-time updates with automatic retry on stall
 * - Stream 2 (Polling): Safety net that periodically queries task status
 * - Stream 3 (Timeout): Global timeout guardian that ensures bounded operation time
 *
 * Key Wonka Operators Used:
 * - pipe(): Compose operators declaratively
 * - map(): Transform subscription data to extract task
 * - filter(): Remove undefined/null tasks and errors from stream
 * - subscribe(): Start stream and get unsubscribe function
 *
 * Stream Lifecycles:
 *
 * 1. Subscription Stream (Stream 1):
 *    - Starts immediately with heartbeat monitoring
 *    - On heartbeat timeout (default 45s): Restart subscription if retries remaining (max 2)
 *    - On subscription error: Count as retry attempt, restart if possible
 *    - On data received: Reset heartbeat, emit data
 *    - Stops when: Task complete, max retries reached, or global timeout
 *
 * 2. Polling Stream (Stream 2):
 *    - Starts after delay (default 30s) as safety net
 *    - Queries at regular interval (default 15s) up to max attempts (default 6)
 *    - Query errors logged but don't stop polling (subscription might still work)
 *    - Provides redundancy if subscription fails completely
 *    - Stops when: Task complete, max attempts reached, or global timeout
 *
 * 3. Timeout Stream (Stream 3):
 *    - Starts immediately with single timer (default 120s / 2 minutes)
 *    - When timeout reached: Cleanup all streams, emit timeout error with diagnostics
 *    - Provides absolute upper bound on operation time
 *    - Stops when: Task complete or timeout elapsed
 *
 * Terminal State Handling:
 * - First stream to receive terminal state (Completed/Failed/Cancelled) wins
 * - Immediately calls cleanupAllStreams() to stop all other streams
 * - Calls onComplete for Completed, onError for Failed/Cancelled
 *
 * Benefits of Three Parallel Streams:
 * - Framework-agnostic (works outside React)
 * - True redundancy: Subscription failure doesn't block progress
 * - Subscription can stall and recover multiple times
 * - Polling provides periodic health checks
 * - Global timeout prevents indefinite waiting
 * - Rich diagnostics on timeout (which streams were active, retry counts, etc.)
 * - Configurable resilience: Tune each stream independently
 * - Type-safe stream transformations
 * - Callback-based for flexibility
 *
 * Configuration Options:
 * - subscriptionTimeoutMs: Time to wait for subscription update before retry (default: 45s)
 * - subscriptionMaxRetries: Max times to restart subscription (default: 2)
 * - pollingIntervalMs: Time between polling queries (default: 15s)
 * - pollingMaxAttempts: Max number of queries (default: 6)
 * - pollingStartDelayMs: When to start polling after init (default: 30s)
 * - globalTimeoutMs: Absolute max time for entire operation (default: 120s / 2 minutes)
 *
 * Usage Example:
 * ```typescript
 * const stream = createResilientGenerativeTaskStream(
 *   client,
 *   'task-id-123',
 *   {
 *     // Optional: Override defaults
 *     subscriptionTimeoutMs: 45000,
 *     subscriptionMaxRetries: 2,
 *     pollingIntervalMs: 15000,
 *     pollingMaxAttempts: 6,
 *     pollingStartDelayMs: 30000,
 *     globalTimeoutMs: 120000,
 *
 *     onData: ({ generativeTask, status, isComplete }) => {
 *       console.log('Task update:', generativeTask.status, 'Stream status:', status);
 *       // status can be: 'subscribing', 'subscription_retrying', 'polling',
 *       //                'completed', 'failed', 'cancelled'
 *     },
 *     onComplete: (generativeTask) => {
 *       console.log('Task completed!', generativeTask);
 *     },
 *     onError: (error, status) => {
 *       console.error('Error during', status, ':', error.message);
 *       // status can be: 'failed', 'cancelled', 'timed_out', 'error'
 *     },
 *   }
 * );
 *
 * const unsubscribe = stream.subscribe();
 *
 * // Later, to stop monitoring:
 * unsubscribe();
 * ```
 *
 * Timeline Example (all times from subscribe() call):
 * 0s:    Start subscription + global timeout
 * 30s:   Start polling (first poll executes immediately)
 * 45s:   Polling: 2nd poll
 * 45s:   Subscription: No data → restart (retry 1/2)
 * 60s:   Polling: 3rd poll
 * 75s:   Polling: 4th poll
 * 90s:   Polling: 5th poll; Subscription: Stalled again → restart (retry 2/2)
 * 105s:  Polling: 6th poll (last)
 * 120s:  Global timeout → cleanup all → emit timeout error
 */
